import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import ProcessDetailView from "./ProcessDetailView";
import { useOrchestrationDetail } from "../hooks/Starters/useOrchestrationDetail";
import { useOrchestrationInstances } from "../hooks/Starters/useOrchestrationInstances";
import { ISubStep } from "../declarations/IFunctionsResponse";
import { IOrchestrationInstanceSummary } from "../declarations/IOrchestrationInstance";

/**
 * Maps parent phase display names to sub-orchestrator function name fragments.
 * The key is the phase name shown in the terminal log; the value is a substring
 * that must appear in the child orchestration's `name` field.
 */
const PHASE_TO_CHILD_NAME: Record<string, string> = {
    "Sync Customer": "SyncCustomer",
    "Sync VAT Number": "SyncVatNum",
    "Update Sales Order Header Charge": "SyncSalesOrderHeaderCharge",
    "Update Sales Order Header": "SyncSalesOrderHeader",
    "Update Sales Order Lines": "SyncSalesOrderLines",
};

/**
 * Ordered keys so that more specific matches (e.g. "SyncSalesOrderHeaderCharge")
 * are tested before less specific ones (e.g. "SyncSalesOrderHeader").
 */
const PHASE_KEYS_ORDERED = [
    "Sync Customer",
    "Sync VAT Number",
    "Update Sales Order Header Charge",
    "Update Sales Order Header",
    "Update Sales Order Lines",
];

function buildChildOrchestrationMap(
    parentInstanceId: string,
    instances: IOrchestrationInstanceSummary[],
): Record<string, ISubStep[]> {
    const map: Record<string, ISubStep[]> = {};

    const children = instances.filter(
        (inst) => inst.customStatus?.parentInstanceId === parentInstanceId,
    );

    for (const child of children) {
        const subSteps = child.customStatus?.subSteps;
        if (!subSteps || subSteps.length === 0) continue;

        for (const phaseKey of PHASE_KEYS_ORDERED) {
            const fragment = PHASE_TO_CHILD_NAME[phaseKey];
            if (child.name.includes(fragment)) {
                // For "Update Sales Order Header", exclude matches that also
                // contain "Charge" to avoid false positives
                if (
                    phaseKey === "Update Sales Order Header" &&
                    child.name.includes("Charge")
                ) {
                    continue;
                }
                map[phaseKey] = subSteps;
                break;
            }
        }
    }

    return map;
}

export default function ProcessDetailDialog() {
    const { instanceId } = useParams<{ instanceId: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const open = location.pathname.includes("/monitor/") && !!instanceId;

    const { data: detail, isLoading } = useOrchestrationDetail(
        instanceId ?? "",
    );

    // Fetch all instances to find child orchestrations.
    // This reuses the same React Query cache as ProcessMonitorPage.
    const { data: instancesData } = useOrchestrationInstances({
        pageSize: 50,
    });

    const childOrchestrations = useMemo(() => {
        if (!detail || !instancesData?.instances) return undefined;

        const map = buildChildOrchestrationMap(
            detail.instanceId,
            instancesData.instances,
        );

        return Object.keys(map).length > 0 ? map : undefined;
    }, [detail, instancesData]);

    const handleClose = () => {
        navigate("/monitor");
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            onClose={handleClose}
            open={open}
            slotProps={{
                paper: {
                    sx: {
                        height: "80vh",
                        maxHeight: "80vh",
                    },
                },
            }}
        >
            <DialogTitle>
                Orchestration Detail
            </DialogTitle>
            <DialogContent>
                {detail ? (
                    <ProcessDetailView
                        childOrchestrations={childOrchestrations}
                        detail={detail}
                        isLoading={isLoading}
                    />
                ) : isLoading ? (
                    <ProcessDetailView
                        detail={null as any}
                        isLoading={true}
                    />
                ) : (
                    <ProcessDetailView
                        detail={null as any}
                        isLoading={false}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
